/**
 * Reference images picker: closing it with the X, Escape or a click outside
 * keeps the photos the user added. Only Cancel throws them away.
 *
 * Why (Shane, Nov 1 2026: "with both Zoie & Little Poo, AI took no notice of
 * the pics loaded before the create video button was clicked"): the picker
 * kept its photos only on Continue. Every other way out (the X, Escape, a
 * click outside, Cancel) dropped what had been uploaded, and the composer
 * showed no sign of it. Zoie_Is_a_Genius (YH8L3xlw4B_AMql01L5rh) was created
 * after about six opens of the picker with no photo, while its description
 * named "@image1", "@image2" and "@image3".
 *
 * A close made while a photo is still uploading, being split or being
 * checked waits for it, the same as Continue, which stays disabled until then:
 * a photo committed before its classifier answers keeps the optimistic role
 * and loses its appearance (the late result only patches the picker's draft).
 */

import type { ReferenceImage } from "@/components/ReferencePickerModal";

/** Kill switch: VITE_REFERENCE_PICKER_CLOSE_KEEPS=false (closing the picker
 * any way but Continue drops its changes again). */
export function referencePickerCloseKeepsEnabled(): boolean {
  return import.meta.env.VITE_REFERENCE_PICKER_CLOSE_KEEPS !== "false";
}

export type ReferencePickerDismissAction =
  /** Nothing changed since the picker opened: just close. */
  | "close"
  /** Keep the changes (as Continue does) and close. */
  | "commit"
  /** A photo is still on its way in or being checked: close when it lands. */
  | "wait";

function referenceSignature(reference: ReferenceImage): string {
  return JSON.stringify([
    reference.imageUrl,
    reference.role,
    reference.name,
    reference.appearance ?? null,
    reference.subjectCount ?? null,
    reference.tag ?? null,
    reference.savedCharacterProjectId ?? null,
    reference.castIdentity ?? null,
    !!reference.classifying,
  ]);
}

/** True when the two lists hold the same photos, settings and order. */
export function sameReferencePickerSelection(
  a: readonly ReferenceImage[],
  b: readonly ReferenceImage[],
): boolean {
  return (
    a.length === b.length &&
    a.every(
      (reference, index) =>
        referenceSignature(reference) === referenceSignature(b[index]),
    )
  );
}

/** What closing the picker without Cancel or Continue should do. */
export function referencePickerDismissAction(input: {
  /** The picker's selection when it opened. */
  openedWith: readonly ReferenceImage[];
  /** Its selection now. */
  draft: readonly ReferenceImage[];
  /** An upload or a group-photo split is running: its photos are not in the
   * draft yet. */
  adding: boolean;
  /** A photo in the draft is still being classified or compared with the
   * main photo (Continue is disabled for the same reason). */
  checking: boolean;
}): ReferencePickerDismissAction {
  if (input.adding) return "wait";
  if (sameReferencePickerSelection(input.openedWith, input.draft)) {
    return "close";
  }
  return input.checking ? "wait" : "commit";
}
